import { Box, Checkbox, FormControlLabel } from "@mui/material";
import React from "react";
import { FieldNode, keyPathToDotDelimitedKey } from "./fieldNodes";

export function renderFieldNodes(
    fields: FieldNode[],
    selectedKeys: Set<string>,
    setSelectedKeys: (keys: Set<string>) => void,
    depth: number,
): React.ReactNode {
    return fields.map((field) => {
        const handleChange = (key: string, checked: boolean) => {
            const newSelectedKeys = new Set(selectedKeys);
            if (checked) {
                newSelectedKeys.add(key);
            } else {
                newSelectedKeys.delete(key);
            }
            setSelectedKeys(newSelectedKeys);
        };

        const handleParentChange = (field: FieldNode, checked: boolean) => {
            const newSelectedKeys = new Set(selectedKeys);

            function toggleChildren(node: FieldNode) {
                if (node.children) {
                    node.children.forEach((child) => toggleChildren(child));
                } else {
                    if (checked) {
                        newSelectedKeys.add(
                            keyPathToDotDelimitedKey(node.path),
                        );
                    } else {
                        newSelectedKeys.delete(
                            keyPathToDotDelimitedKey(node.path),
                        );
                    }
                }
            }

            toggleChildren(field);
            setSelectedKeys(newSelectedKeys);
        };

        const getChildKeys = (node: FieldNode): string[] => {
            if (!node.children) return [keyPathToDotDelimitedKey(node.path)];
            return node.children.flatMap((child) => getChildKeys(child));
        };

        const childKeys = field.children ? getChildKeys(field) : [];
        const allChildrenSelected =
            childKeys.length > 0 &&
            childKeys.every((key) => selectedKeys.has(key));
        const someChildrenSelected =
            childKeys.length > 0 &&
            childKeys.some((key) => selectedKeys.has(key));

        if (field.children) {
            return (
                <Box key={field.key} sx={{ ml: depth * 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={allChildrenSelected}
                                indeterminate={
                                    someChildrenSelected && !allChildrenSelected
                                }
                                onChange={(e) =>
                                    handleParentChange(field, e.target.checked)
                                }
                                size="small"
                            />
                        }
                        label={field.label}
                        sx={{ ml: 0, fontWeight: "bold" }}
                    />
                    {renderFieldNodes(
                        field.children,
                        selectedKeys,
                        setSelectedKeys,
                        depth + 1,
                    )}
                </Box>
            );
        } else {
            return (
                <Box key={field.key} sx={{ ml: depth * 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedKeys.has(
                                    keyPathToDotDelimitedKey(field.path),
                                )}
                                onChange={(e) =>
                                    handleChange(
                                        keyPathToDotDelimitedKey(field.path),
                                        e.target.checked,
                                    )
                                }
                                size="small"
                            />
                        }
                        label={field.label}
                        sx={{ ml: 0 }}
                    />
                </Box>
            );
        }
    });
}
